import React, { useState, useEffect, useCallback } from 'react';
import { reportService } from '../../services/reportService';
import { Download, AlertTriangle, AlertCircle } from 'lucide-react';
import SortableHeader, { useSort } from './SortableHeader';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

const UnbilledActivityReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [minDays, setMinDays] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reportService.getUnbilledActivity({ min_days_unbilled: minDays });
      setData(response.data);
    } catch (err) {
      console.error('Error fetching unbilled activity:', err);
      setError('Failed to load Unbilled Activity report');
    } finally {
      setLoading(false);
    }
  }, [minDays]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { sortConfig, requestSort, sortItems } = useSort('days_unbilled', 'desc');

  const handleExportCSV = () => {
    reportService.downloadCSV(reportService.getUnbilledActivity, { min_days_unbilled: minDays }, 'unbilled_activity_report.csv');
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-64 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
        <p className="text-gray-500">{error}</p>
        <button onClick={fetchData} className="mt-2 text-yellow-600 hover:text-yellow-800 text-sm">Retry</button>
      </div>
    );
  }

  const { summary, items } = data || { summary: {}, items: [] };
  const sortedItems = sortItems(items);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Unbilled Activity Finder</h2>
        <button onClick={handleExportCSV} className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {/* High-value alert */}
      {summary.high_value_flag && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-yellow-800">Revenue Opportunity Detected</h4>
            <p className="text-sm text-yellow-700 mt-1">
              You have {formatCurrency(summary.total_unbilled_amount || 0)} in unbilled work
              ({parseFloat(summary.total_unbilled_hours || 0).toFixed(1)} hours) across {summary.cases_with_unbilled || 0} cases.
              Review these items and create invoices to capture this revenue.
            </p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-500">Minimum days unbilled:</span>
        {[0, 7, 14, 30, 60].map((d) => (
          <button
            key={d}
            onClick={() => setMinDays(d)}
            className={`px-3 py-1 text-sm rounded font-medium transition-colors ${
              minDays === d ? 'bg-yellow-100 text-yellow-800' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {d === 0 ? 'All' : `${d}+ days`}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-yellow-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Total Unbilled</p>
          <p className="text-lg font-bold text-yellow-700">{formatCurrency(summary.total_unbilled_amount || 0)}</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Unbilled Hours</p>
          <p className="text-lg font-bold text-blue-700">{parseFloat(summary.total_unbilled_hours || 0).toFixed(1)}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Time Entries</p>
          <p className="text-lg font-bold text-gray-900">{summary.time_entry_count || 0}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Expenses</p>
          <p className="text-lg font-bold text-gray-900">{summary.expense_count || 0}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Cases Affected</p>
          <p className="text-lg font-bold text-gray-900">{summary.cases_with_unbilled || 0}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Type" sortKey="activity_type" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Case" sortKey="case_number" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Client" sortKey="client_name" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Description" sortKey="description" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Attorney" sortKey="attorney_name" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Hours" sortKey="hours" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Amount" sortKey="amount" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Days" sortKey="days_unbilled" sortConfig={sortConfig} onSort={requestSort} align="right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedItems.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No unbilled activity found</td></tr>
              ) : sortedItems.map((item) => (
                <tr key={`${item.activity_type}-${item.activity_id}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <TypeBadge type={item.activity_type} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.case_number || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.client_name || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">{item.description}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.attorney_name || '-'}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">
                    {item.hours != null ? parseFloat(item.hours).toFixed(1) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(item.amount)}</td>
                  <td className="px-4 py-3 text-sm text-right">
                    <span className={item.days_unbilled > 30 ? 'text-red-600 font-medium' : 'text-gray-700'}>
                      {item.days_unbilled}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const TypeBadge = ({ type }) => {
  const config = {
    time_entry: { label: 'Time', color: 'bg-blue-100 text-blue-800' },
    expense: { label: 'Expense', color: 'bg-purple-100 text-purple-800' },
    task: { label: 'Task', color: 'bg-green-100 text-green-800' },
  };
  const { label, color } = config[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${color}`}>{label}</span>
  );
};

export default UnbilledActivityReport;
